import * as React from 'react'

import { Clock } from 'lucide-react'
import { observer } from 'mobx-react'

import Avatar from '~/client/src/shared/components/Avatar/Avatar'
import AvatarType from '~/client/src/shared/enums/AvatarType'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import ProjectDateStore from '~/client/src/shared/stores/ui/ProjectDate.store'

import { getCollageLayout } from '../core/collage'
import { splitMessageSegments } from '../core/protocol/mentionText'
import { Attachment, ChatMessage, messageKey } from '../core/protocol/types'
import { ContactsCache } from '../core/stores/ContactsCache'

const BUBBLE_RADIUS = 18
const BUBBLE_ANCHOR_RADIUS = 5
const COLLAGE_ENVELOPE_WIDTH = 240

interface IProps {
  message: ChatMessage
  isOwn: boolean
  isFirstOfRun: boolean
  isLastOfRun: boolean
  showAuthor: boolean
  showAvatar: boolean
  userId: string
  contacts: ContactsCache
  isAuthorOnline: boolean
  projectDateStore: ProjectDateStore
  onRetry: (fakeId: string) => void
  onMentionPress: (mentionedUserId: string) => void
  onAttachmentPress: (attachments: readonly Attachment[], index: number) => void
  belowBubble?: React.ReactNode
}

function MessageBubble({
  message,
  isOwn,
  isFirstOfRun,
  isLastOfRun,
  showAuthor,
  showAvatar,
  userId,
  contacts,
  isAuthorOnline,
  projectDateStore,
  onRetry,
  onMentionPress,
  onAttachmentPress,
  belowBubble,
}: IProps): React.ReactElement {
  const hasTextBelowImage = !!message.text
  const isFailed = message.state === 'failed'

  const renderMessageText = (): React.ReactElement => (
    <p className="tw:m-0 tw:whitespace-pre-wrap tw:wrap-anywhere tw:text-[15px] tw:leading-[21px]">
      {splitMessageSegments(message.text).map((segment, index) => {
        if (segment.url) {
          return (
            <a
              key={index}
              href={segment.url}
              target="_blank"
              rel="noopener noreferrer"
              onClick={event => event.stopPropagation()}
              className={
                isOwn
                  ? 'tw:underline tw:text-primary-foreground'
                  : 'tw:underline tw:text-primary'
              }
            >
              {segment.text}
            </a>
          )
        }
        if (!segment.userId) {
          return <span key={index}>{segment.text}</span>
        }

        const isSelfMention = segment.userId === userId
        const name =
          contacts.getFullName(segment.userId) || Localization.translator.user

        return (
          <span
            key={index}
            onClick={event => {
              event.stopPropagation()
              onMentionPress(segment.userId)
            }}
            className={[
              'tw:cursor-pointer tw:font-semibold',
              isOwn ? 'tw:text-primary-foreground' : 'tw:text-primary',
              isSelfMention &&
                (isOwn
                  ? 'tw:rounded tw:bg-primary-foreground/25'
                  : 'tw:rounded tw:bg-primary/15'),
            ]
              .filter(Boolean)
              .join(' ')}
          >
            @{name}
          </span>
        )
      })}
    </p>
  )

  const renderAttachments = (): React.ReactElement => {
    if (!message.attachments.length) {
      return null
    }

    const layout = getCollageLayout(message.attachments, COLLAGE_ENVELOPE_WIDTH)

    return (
      <div
        className={`tw:relative tw:overflow-hidden ${
          message.text ? 'tw:mb-1' : ''
        }`}
        style={{
          width: layout.width,
          height: layout.height,
          maxWidth: '100%',
          borderRadius: BUBBLE_RADIUS - 6,
        }}
      >
        {layout.tiles.map(tile => (
          <button
            key={tile.index}
            type="button"
            onClick={event => {
              event.stopPropagation()
              onAttachmentPress(message.attachments, tile.index)
            }}
            className="tw:absolute tw:cursor-pointer tw:border-0 tw:p-0"
            style={{
              left: tile.left,
              top: tile.top,
              width: tile.width,
              height: tile.height,
            }}
          >
            <img
              src={message.attachments[tile.index].url}
              alt={message.attachments[tile.index].fileName}
              className="tw:h-full tw:w-full tw:object-cover"
            />
            {tile.index === 3 && layout.overlayCount > 0 && (
              <span className="tw:absolute tw:inset-0 tw:flex tw:items-center tw:justify-center tw:bg-black/40 tw:text-2xl tw:font-semibold tw:text-white">
                +{layout.overlayCount}
              </span>
            )}
          </button>
        ))}
        <span className="tw:pointer-events-none tw:absolute tw:bottom-1 tw:right-1.5 tw:rounded-lg tw:bg-black/45 tw:px-1.5 tw:text-[10px] tw:text-white">
          {projectDateStore.getTimeToDisplay(message.createdAt)}
        </span>
      </div>
    )
  }

  const renderBubbleMeta = (): React.ReactElement => {
    const metaClass = isOwn
      ? 'tw:text-primary-foreground/70'
      : 'tw:text-muted-foreground'

    if (message.state === 'sending') {
      return <Clock size={9} className={`tw:mt-1 tw:self-end ${metaClass}`} />
    }

    if (message.state === 'failed') {
      return null
    }

    return (
      <span className={`tw:mt-0.5 tw:self-end tw:text-[10px] ${metaClass}`}>
        {projectDateStore.getTimeToDisplay(message.createdAt)}
      </span>
    )
  }

  const bubbleStyle: React.CSSProperties = {
    borderRadius: BUBBLE_RADIUS,
    padding: message.attachments.length ? 6 : '8px 13px',
    ...(isOwn
      ? {
          borderBottomRightRadius: isLastOfRun
            ? BUBBLE_ANCHOR_RADIUS
            : BUBBLE_RADIUS,
        }
      : {
          borderBottomLeftRadius: isLastOfRun
            ? BUBBLE_ANCHOR_RADIUS
            : BUBBLE_RADIUS,
        }),
  }

  return (
    <div
      className={[
        'tw:flex tw:items-end',
        isOwn ? 'tw:justify-end' : 'tw:justify-start',
        isFirstOfRun ? 'tw:mt-3' : 'tw:mt-[3px]',
        message.state === 'sending' && 'tw:opacity-60',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      {showAvatar && (
        <div className="tw:w-10 tw:shrink-0">
          {isLastOfRun && (
            <Avatar
              avatarType={AvatarType.User}
              isOnline={isAuthorOnline}
              src={contacts.get(message.authorId)?.avatarUrl}
            />
          )}
        </div>
      )}
      <div
        className={`tw:flex tw:min-w-0 tw:max-w-[78%] tw:flex-col ${
          isOwn ? 'tw:items-end' : 'tw:items-start'
        }`}
      >
        {showAuthor && (
          <span className="tw:pb-0.5 tw:pl-2.5 tw:text-xs tw:font-semibold tw:text-primary">
            {contacts.getFullName(message.authorId) || '…'}
          </span>
        )}
        <div
          onClick={isFailed ? () => onRetry(messageKey(message)) : undefined}
          className={[
            'tw:flex tw:flex-col',
            isOwn
              ? 'tw:bg-primary tw:text-primary-foreground'
              : 'tw:bg-muted tw:text-foreground',
            isFailed && 'tw:cursor-pointer tw:border tw:border-destructive',
          ]
            .filter(Boolean)
            .join(' ')}
          style={bubbleStyle}
        >
          {renderAttachments()}
          {!!message.text && (
            <div className="tw:flex tw:flex-col">
              {renderMessageText()}
              {(!message.attachments.length || hasTextBelowImage) &&
                renderBubbleMeta()}
            </div>
          )}
        </div>
        {belowBubble}
        {isFailed && (
          <span className="tw:mt-0.5 tw:pr-1 tw:text-[11px] tw:text-destructive">
            {Localization.translator.notSentTapToRetry}
          </span>
        )}
      </div>
    </div>
  )
}

export default observer(MessageBubble)
